// =============================================================================
// VINUM - Catalog Store
// EXACT match with Flutter CatalogPage behavior
// Uses customer identifier (fiscalCode/vatNumber) for personalized pricing
//
// FLUTTER BEHAVIOR (source of truth):
// - ONE API call at init loads ALL products into _allProducts
// - Filters are applied CLIENT-SIDE on _allProducts (no new API calls)
// - Desktop: real-time filtering on onChange
// - Mobile: filtering on "Applica" button click
// - Pagination is done via _visibleCount (infinite scroll shows more items)
// =============================================================================

import { create } from 'zustand';
import type { CatalogItem, CatalogFilter } from '../types';
import { CatalogAPI } from '../api';
import { useAuthStore } from './auth.store';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface CatalogState {
  // Flutter: _allProducts - all products loaded from API (never changes after init)
  allItems: CatalogItem[];
  // Flutter: _filteredProducts - result of _computeFiltered()
  filteredItems: CatalogItem[];
  // Flutter: visible items = _filteredProducts.take(_visibleCount)
  items: CatalogItem[];
  selectedItem: CatalogItem | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;

  // Flutter: _visibleCount (for infinite scroll pagination)
  visibleCount: number;
  hasMore: boolean;

  // Filter state
  filter: CatalogFilter;

  // Categories & Brands for filters (extracted from allItems)
  categories: string[];
  brands: string[];
  priceRange: { min: number; max: number } | null;
}

interface CatalogActions {
  // Data loading - ONE API call loads all data
  loadAllItems: () => Promise<void>;
  loadItem: (itemId: string) => Promise<CatalogItem>;
  refreshItems: () => Promise<void>;

  // Flutter: _applyFiltersLocked() - filters in memory, NO API call
  applyFiltersLocal: () => void;

  // Flutter: _extendVisible() - show more items (infinite scroll)
  loadMoreItems: () => void;

  // Filtering (local state only, no API)
  setFilter: (filter: Partial<CatalogFilter>) => void;
  clearFilter: () => void;

  // Backward compatibility aliases
  loadItems: () => Promise<void>;
  applyFilter: () => void;

  // Filter options (extracted from loaded data)
  loadCategories: () => Promise<void>;
  loadBrands: () => Promise<void>;
  loadPriceRange: () => Promise<void>;

  // State management
  setSelectedItem: (item: CatalogItem | null) => void;
  clearError: () => void;
}

type CatalogStore = CatalogState & CatalogActions;

// -----------------------------------------------------------------------------
// Constants - Flutter exact values
// -----------------------------------------------------------------------------

// Flutter: _pageSize = 12
const FLUTTER_PAGE_SIZE = 12;

const initialFilter: CatalogFilter = {
  page: 1,
  limit: FLUTTER_PAGE_SIZE,
  sortBy: undefined, // Flutter: 'Consigliati' = no sort
  sortOrder: 'asc',
};

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

/**
 * Get customer identifier for personalized pricing (Flutter exact logic)
 * Priority: fiscalCode > vatNumber
 */
function getCustomerIdentifier(): string | undefined {
  const user = useAuthStore.getState().user;
  if (!user) return undefined;

  const customer = (user as any).customer;
  if (!customer) return undefined;

  return customer.fiscalCode || customer.vatNumber || undefined;
}

/**
 * Flutter: _computeFiltered()
 * Apply filters to allItems and return filtered list
 */
function computeFiltered(allItems: CatalogItem[], filter: CatalogFilter): CatalogItem[] {
  if (allItems.length === 0) return [];

  let result = [...allItems];

  // Flutter: Text search on name and winery
  if (filter.q) {
    const query = filter.q.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (item) =>
          item.itemDescription.toLowerCase().includes(query) ||
          (item.brand?.toLowerCase().includes(query) ?? false)
      );
    }
  }

  // Flutter: typeMatch = _selectedType == 'Tutti' || p['typeKey'] == _selectedType
  if (filter.category && filter.category !== 'Tutti') {
    result = result.filter((item) => item.category === filter.category);
  }

  // Flutter: brand/winery filter (exact match)
  if (filter.brand) {
    result = result.filter((item) => item.brand === filter.brand);
  }

  // Flutter: priceMatch = price >= start && price <= end
  // Uses resolvedPrice (stored in price1 after transform)
  if (filter.minPrice != null) {
    result = result.filter((item) => (item.price1 ?? 0) >= filter.minPrice!);
  }
  if (filter.maxPrice != null) {
    result = result.filter((item) => (item.price1 ?? 0) <= filter.maxPrice!);
  }

  // Flutter: bioMatch = !_onlyBio || p['isBio'] == true
  if (filter.isBio === true) {
    result = result.filter((item) => item.specs?.isBio === true);
  }

  // Flutter: km0Match = !_onlyKm0 || p['isKm0'] == true
  if (filter.isKm0 === true) {
    result = result.filter((item) => item.specs?.isKm0 === true);
  }

  return result;
}

/**
 * Flutter: _sortProducts()
 * Sort filtered products based on sortKey
 */
function sortProducts(items: CatalogItem[], sortBy?: string, sortOrder?: 'asc' | 'desc'): CatalogItem[] {
  if (!sortBy || sortBy === 'recommended') {
    // Flutter: 'Consigliati' - no sorting, return original order
    return items;
  }

  const sorted = [...items];
  const order = sortOrder === 'desc' ? -1 : 1;

  sorted.sort((a, b) => {
    if (sortBy === 'price') {
      // Flutter: 'Prezzo crescente' / 'Prezzo decrescente'
      const pa = a.price1 ?? 0;
      const pb = b.price1 ?? 0;
      return (pa - pb) * order;
    }
    if (sortBy === 'name') {
      // Flutter: 'Nome'
      return a.itemDescription.localeCompare(b.itemDescription) * order;
    }
    return 0;
  });

  return sorted;
}

/**
 * Extract unique categories from items (Flutter: wine types from groupDescription)
 */
function extractCategories(items: CatalogItem[]): string[] {
  const categories = new Set<string>();
  items.forEach((item) => {
    if (item.category) {
      categories.add(item.category);
    }
  });
  return Array.from(categories).sort();
}

/**
 * Extract unique brands/wineries from items
 */
function extractBrands(items: CatalogItem[]): string[] {
  const brands = new Set<string>();
  items.forEach((item) => {
    if (item.brand) {
      brands.add(item.brand);
    }
  });
  return Array.from(brands).sort();
}

/**
 * Extract price range from items
 */
function extractPriceRange(items: CatalogItem[]): { min: number; max: number } {
  const prices = items
    .map((item) => item.price1 ?? 0)
    .filter((p) => p > 0);

  if (prices.length === 0) {
    return { min: 0, max: 100 };
  }

  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
  };
}

// -----------------------------------------------------------------------------
// Store Implementation
// -----------------------------------------------------------------------------

export const useCatalogStore = create<CatalogStore>()((set, get) => ({
  // Initial state
  allItems: [],
  filteredItems: [],
  items: [],
  selectedItem: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,

  visibleCount: FLUTTER_PAGE_SIZE,
  hasMore: false,

  filter: { ...initialFilter },

  categories: [],
  brands: [],
  priceRange: null,

  // ==========================================================================
  // Flutter: _loadCatalogs() - ONE API call loads ALL products
  // ==========================================================================
  loadAllItems: async () => {
    set({ isLoading: true, error: null });

    try {
      const identifier = getCustomerIdentifier();

      // Flutter: Single API call to get ALL availabilities
      // No filter params sent to API - all filtering is client-side
      const response = await CatalogAPI.getItems(
        { page: 1, limit: 10000 }, // Get all items (no server pagination)
        identifier
      );

      const allItems = response.items;

      // Extract filter options from loaded data
      const categories = extractCategories(allItems);
      const brands = extractBrands(allItems);
      const priceRange = extractPriceRange(allItems);

      // Apply initial filters and get visible items
      const currentFilter = get().filter;
      const filtered = computeFiltered(allItems, currentFilter);
      const sorted = sortProducts(filtered, currentFilter.sortBy, currentFilter.sortOrder);
      const visible = sorted.slice(0, FLUTTER_PAGE_SIZE);

      set({
        allItems,
        filteredItems: sorted,
        items: visible,
        visibleCount: FLUTTER_PAGE_SIZE,
        hasMore: sorted.length > FLUTTER_PAGE_SIZE,
        isLoading: false,
        categories,
        brands,
        priceRange,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Errore caricamento catalogo';
      set({ isLoading: false, error: message });
    }
  },

  // ==========================================================================
  // Flutter: _applyFiltersLocked() - filter in memory, NO API call
  // ==========================================================================
  applyFiltersLocal: () => {
    const { allItems, filter } = get();

    // Flutter: _filteredProducts = _computeFiltered()
    const filtered = computeFiltered(allItems, filter);
    const sorted = sortProducts(filtered, filter.sortBy, filter.sortOrder);

    // Flutter: _visibleCount = _pageSize.clamp(0, _filteredProducts.length)
    const visibleCount = Math.min(FLUTTER_PAGE_SIZE, sorted.length);
    const visible = sorted.slice(0, visibleCount);

    set({
      filteredItems: sorted,
      items: visible,
      visibleCount,
      hasMore: sorted.length > visibleCount,
    });
  },

  // ==========================================================================
  // Flutter: _extendVisible() - infinite scroll "load more"
  // ==========================================================================
  loadMoreItems: () => {
    const { filteredItems, visibleCount, isLoadingMore } = get();

    if (isLoadingMore || visibleCount >= filteredItems.length) return;

    set({ isLoadingMore: true });

    // Flutter: _visibleCount = (_visibleCount + _pageSize).clamp(0, _filteredProducts.length)
    const newVisibleCount = Math.min(visibleCount + FLUTTER_PAGE_SIZE, filteredItems.length);
    const visible = filteredItems.slice(0, newVisibleCount);

    set({
      items: visible,
      visibleCount: newVisibleCount,
      hasMore: newVisibleCount < filteredItems.length,
      isLoadingMore: false,
    });
  },

  loadItem: async (itemId: string) => {
    set({ isLoading: true, error: null });

    try {
      const identifier = getCustomerIdentifier();
      const item = await CatalogAPI.getItemByItemId(itemId, identifier);
      set({ selectedItem: item, isLoading: false });
      return item;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Errore caricamento prodotto';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  refreshItems: async () => {
    await get().loadAllItems();
  },

  // ==========================================================================
  // Filter Management (local state only)
  // ==========================================================================

  setFilter: (newFilter: Partial<CatalogFilter>) => {
    const currentFilter = get().filter;
    set({
      filter: { ...currentFilter, ...newFilter },
    });
    // Note: Does NOT auto-apply - caller decides when to apply
    // Desktop: call applyFiltersLocal() immediately after
    // Mobile: call applyFiltersLocal() on "Applica" button click
  },

  clearFilter: () => {
    const { priceRange } = get();
    set({
      filter: {
        ...initialFilter,
        // Flutter: reset price range to min/max of all products
        minPrice: priceRange?.min,
        maxPrice: priceRange?.max,
      },
    });
    get().applyFiltersLocal();
  },

  // ==========================================================================
  // Backward Compatibility Aliases
  // ==========================================================================

  loadItems: async () => {
    await get().loadAllItems();
  },

  applyFilter: () => {
    get().applyFiltersLocal();
  },

  // ==========================================================================
  // Filter Options (already loaded from allItems)
  // ==========================================================================

  loadCategories: async () => {
    // Categories are already extracted in loadAllItems()
    // This is kept for backward compatibility
    const { allItems } = get();
    if (allItems.length > 0) {
      const categories = extractCategories(allItems);
      set({ categories });
    }
  },

  loadBrands: async () => {
    // Brands are already extracted in loadAllItems()
    const { allItems } = get();
    if (allItems.length > 0) {
      const brands = extractBrands(allItems);
      set({ brands });
    }
  },

  loadPriceRange: async () => {
    // Price range is already extracted in loadAllItems()
    const { allItems } = get();
    if (allItems.length > 0) {
      const priceRange = extractPriceRange(allItems);
      set({ priceRange });
    }
  },

  setSelectedItem: (item: CatalogItem | null) => {
    set({ selectedItem: item });
  },

  clearError: () => {
    set({ error: null });
  },
}));

// -----------------------------------------------------------------------------
// Selectors
// -----------------------------------------------------------------------------

export const selectCatalogItems = (state: CatalogStore) => state.items;
export const selectSelectedItem = (state: CatalogStore) => state.selectedItem;
export const selectCatalogIsLoading = (state: CatalogStore) => state.isLoading;
export const selectCatalogError = (state: CatalogStore) => state.error;
export const selectCatalogFilter = (state: CatalogStore) => state.filter;
export const selectCatalogHasMore = (state: CatalogStore) => state.hasMore;
export const selectCatalogCategories = (state: CatalogStore) => state.categories;
export const selectCatalogBrands = (state: CatalogStore) => state.brands;
export const selectCatalogPriceRange = (state: CatalogStore) => state.priceRange;
